import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  resetPasswordSchema,
  confirmResetPasswordSchema,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from '@shared/validation/auth-schemas';
import { requestPasswordReset, confirmPasswordReset } from '../../lib/auth';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

type Step = 'request' | 'confirm' | 'done';

export function PasswordRecoveryPage() {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestForm = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const confirmForm = useForm<ConfirmResetPasswordInput>({
    resolver: zodResolver(confirmResetPasswordSchema),
  });

  const handleRequest = async (data: ResetPasswordInput) => {
    setError('');
    setIsSubmitting(true);
    try {
      await requestPasswordReset(data.email);
      setEmail(data.email);
      setStep('confirm');
    } catch {
      setError('No se pudo enviar el código. Verifique el correo e intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (data: ConfirmResetPasswordInput) => {
    setError('');
    setIsSubmitting(true);
    try {
      await confirmPasswordReset(data.email, data.code, data.newPassword);
      setStep('done');
    } catch {
      setError('Código inválido o expirado. Intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="blob-blue" />
      <div className="blob-orange" />
      <div className="blob-dark" />

      <Card className="relative z-10 w-full max-w-md fade-up" padding="lg">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-brand-dark">Recuperar Contraseña</h1>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === 'request' && (
          <form onSubmit={requestForm.handleSubmit(handleRequest)} className="space-y-4">
            <p className="text-sm text-gray-500">
              Ingrese su correo electrónico y le enviaremos un código de verificación.
            </p>
            <Input
              label="Correo electrónico"
              type="email"
              placeholder="usuario@octopaint.com"
              error={requestForm.formState.errors.email?.message}
              disabled={isSubmitting}
              {...requestForm.register('email')}
            />
            <Button type="submit" fullWidth isLoading={isSubmitting} loadingText="Enviando...">
              Enviar Código
            </Button>
          </form>
        )}

        {step === 'confirm' && (
          <form onSubmit={confirmForm.handleSubmit(handleConfirm)} className="space-y-4">
            <p className="text-sm text-gray-500">
              Ingrese el código enviado a <strong>{email}</strong> y su nueva contraseña.
            </p>
            <input type="hidden" value={email} {...confirmForm.register('email')} />
            <Input
              label="Código de verificación"
              placeholder="123456"
              error={confirmForm.formState.errors.code?.message}
              disabled={isSubmitting}
              {...confirmForm.register('code')}
            />
            <Input
              label="Nueva contraseña"
              type="password"
              placeholder="••••••••"
              error={confirmForm.formState.errors.newPassword?.message}
              disabled={isSubmitting}
              {...confirmForm.register('newPassword')}
            />
            <Button type="submit" fullWidth isLoading={isSubmitting} loadingText="Confirmando...">
              Cambiar Contraseña
            </Button>
          </form>
        )}

        {step === 'done' && (
          <div className="text-center space-y-4">
            <p className="text-sm text-green-600 font-medium">
              Su contraseña ha sido actualizada exitosamente.
            </p>
            <Link to="/login">
              <Button fullWidth>Ir al Login</Button>
            </Link>
          </div>
        )}

        {step !== 'done' && (
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-brand-blue hover:underline">
              Volver al login
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
